// @ts-nocheck

import { Fragment, type ReactNode } from "react";

import { decode } from "he";

import { Anchor } from "@atlassiansox/bxp-content-framework";

import { useAnalytics, useAnalyticsAttributes } from "./hooks";

// Simple markdown parser that maps markdown to React elements
// Similar to RichText component pattern
export const Markdown = ({
    children = "",
    links = true
}: {
    children?: string;
    links?: boolean;
}) => {
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    if (!children || !children.trim()) {
        return null;
    }

    const content = children
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;nbsp;/g, " ")
        .replace(/\u00A0/g, " ")
        .trim();

    if (!content) {
        return null;
    }

    return <>{parseMarkdown(content)}</>;

    function parseMarkdown(text: string): ReactNode[] {
        const lines = text.split("\n").filter((line) => {
            const cleaned = line
                .replace(/&nbsp;/g, "")
                .replace(/\u00A0/g, "")
                .trim();
            return cleaned.length > 0;
        });

        const elements: ReactNode[] = [];
        let listItems: ReactNode[] = [];
        let listKey = 0;
        let blockquoteLines: ReactNode[] = [];
        let blockquoteKey = 0;

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(<ul key={`list-${listKey++}`}>{listItems}</ul>);
                listItems = [];
            }
        };

        const flushBlockquote = () => {
            if (blockquoteLines.length > 0) {
                elements.push(
                    <blockquote key={`blockquote-${blockquoteKey++}`}>
                        {blockquoteLines}
                    </blockquote>
                );
                blockquoteLines = [];
            }
        };

        lines.forEach((line, lineIndex) => {
            const lineElements = mapLineToReactElement(line, lineIndex);

            // Check if this line is a list item
            const trimmedLine = line.trim();
            const isListItem = /^[\*\-\+]\s+/.test(trimmedLine);
            const isBlockquote = /^>\s*/.test(trimmedLine);

            if (isBlockquote) {
                // Flush any pending lists
                flushList();
                // Extract blockquote content (remove the > marker)
                const blockquoteContent = trimmedLine.replace(/^>\s*/, "");
                if (blockquoteContent) {
                    const children = parseInlineContent(blockquoteContent);
                    blockquoteLines.push(
                        <p key={`bq-p-${lineIndex}`}>{children}</p>
                    );
                }
            } else if (isListItem) {
                // Flush any pending blockquotes
                flushBlockquote();
                // Extract list item content (remove the bullet marker)
                const listContent = trimmedLine.replace(/^[\*\-\+]\s+/, "");
                const children = parseInlineContent(listContent);
                listItems.push(<li key={`li-${lineIndex}`}>{children}</li>);
            } else {
                // Not a list item or blockquote, flush any pending
                flushList();
                flushBlockquote();
                elements.push(...lineElements);
            }
        });

        // Flush any remaining list items and blockquotes
        flushList();
        flushBlockquote();

        return elements;
    }

    function mapLineToReactElement(line: string, index: number): ReactNode[] {
        const trimmedLine = line.trim();

        // Skip list items - they're handled in parseMarkdown
        if (/^[\*\-\+]\s+/.test(trimmedLine)) {
            return [];
        }

        // Skip blockquotes - they're handled in parseMarkdown
        if (/^>\s*/.test(trimmedLine)) {
            return [];
        }

        // Thematic break (e.g. ___, --- , ***)
        if (/^(?:_{3,}|\*{3,}|-{3,})$/.test(trimmedLine)) {
            return [<hr key={`hr-${index}`} role="presentation" />];
        }

        // Check for headers
        const headerMatch = /^(#{1,6})\s+(.+)$/.exec(trimmedLine);
        if (headerMatch) {
            const level = headerMatch[1].length;
            const content = headerMatch[2];
            const children = parseInlineContent(content);

            switch (level) {
                case 1:
                    return [<h1 key={`h1-${index}`}>{children}</h1>];
                case 2:
                    return [<h2 key={`h2-${index}`}>{children}</h2>];
                case 3:
                    return [<h3 key={`h3-${index}`}>{children}</h3>];
                case 4:
                    return [<h4 key={`h4-${index}`}>{children}</h4>];
                case 5:
                    return [<h5 key={`h5-${index}`}>{children}</h5>];
                case 6:
                    return [<h6 key={`h6-${index}`}>{children}</h6>];
            }
        }

        // Regular paragraph content - wrap in <p> tag for proper spacing
        const children = parseInlineContent(trimmedLine);
        return children.length > 0
            ? [<p key={`p-${index}`}>{children}</p>]
            : [];
    }

    function parseInlineContent(text: string): ReactNode[] {
        const cleaned = text.replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ");

        const keyRef = { value: 0 };
        return parseInlineSegment(cleaned, keyRef);

        // eslint-disable-next-line complexity
        function parseInlineSegment(
            segment: string,
            keyRef: { value: number }
        ): ReactNode[] {
            const nodes: ReactNode[] = [];
            let i = 0;
            let textStart = 0;

            const pushText = (end: number) => {
                if (end <= textStart) return;
                const textContent = segment.slice(textStart, end);
                nodes.push(
                    <Fragment key={`text-${keyRef.value++}`}>
                        {decode(textContent)}
                    </Fragment>
                );
                textStart = end;
            };

            while (i < segment.length) {
                // Handle escape sequences (e.g., \*, \[, \], etc.)
                if (segment[i] === "\\" && i + 1 < segment.length) {
                    const nextChar = segment[i + 1];
                    // Check if next character is a special markdown character
                    if (
                        ["*", "_", "[", "]", "(", ")", "\\"].includes(nextChar)
                    ) {
                        pushText(i);
                        // Skip the backslash and just push the escaped character
                        nodes.push(
                            <Fragment key={`text-${keyRef.value++}`}>
                                {nextChar}
                            </Fragment>
                        );
                        i += 2;
                        textStart = i;
                        continue;
                    }
                }

                // Images: ![alt](src)
                if (
                    segment[i] === "!" &&
                    i + 1 < segment.length &&
                    segment[i + 1] === "["
                ) {
                    const endText = segment.indexOf("]", i + 2);
                    const hasParen =
                        endText !== -1 && segment[endText + 1] === "(";
                    if (hasParen) {
                        const endHref = segment.indexOf(")", endText + 2);
                        if (endHref !== -1) {
                            const altText = segment.slice(i + 2, endText);
                            const src = segment.slice(endText + 2, endHref);

                            pushText(i);

                            nodes.push(
                                // eslint-disable-next-line no-restricted-syntax
                                <img
                                    key={`img-${keyRef.value++}`}
                                    src={src}
                                    alt={altText}
                                />
                            );

                            i = endHref + 1;
                            textStart = i;
                            continue;
                        }
                    }
                }

                // Links: [text](href) or clickable images: [![alt](imgSrc)](linkHref)
                if (segment[i] === "[") {
                    // Find the matching closing bracket, accounting for nested brackets
                    let bracketCount = 1;
                    let endText = -1;
                    for (
                        let j = i + 1;
                        j < segment.length && bracketCount > 0;
                        j++
                    ) {
                        if (segment[j] === "[" && segment[j - 1] !== "\\") {
                            bracketCount++;
                        } else if (
                            segment[j] === "]" &&
                            segment[j - 1] !== "\\"
                        ) {
                            bracketCount--;
                            if (bracketCount === 0) {
                                endText = j;
                                break;
                            }
                        }
                    }

                    const hasParen =
                        endText !== -1 && segment[endText + 1] === "(";
                    if (hasParen) {
                        // Find the matching closing paren, accounting for nested parens
                        let parenCount = 1;
                        let endHref = -1;
                        for (
                            let j = endText + 2;
                            j < segment.length && parenCount > 0;
                            j++
                        ) {
                            if (segment[j] === "(" && segment[j - 1] !== "\\") {
                                parenCount++;
                            } else if (
                                segment[j] === ")" &&
                                segment[j - 1] !== "\\"
                            ) {
                                parenCount--;
                                if (parenCount === 0) {
                                    endHref = j;
                                    break;
                                }
                            }
                        }

                        if (endHref !== -1) {
                            const linkText = segment.slice(i + 1, endText);
                            // Extract href and optional title (e.g., "url" or "url \"title\"")
                            const hrefContent = segment.slice(
                                endText + 2,
                                endHref
                            );
                            const hrefMatch = /^([^\s"]+)(?:\s+"[^"]*")?$/.exec(
                                hrefContent
                            );
                            const href = hrefMatch ? hrefMatch[1] : hrefContent;

                            pushText(i);

                            // Check if linkText is an image: ![alt](src)
                            const imageMatch =
                                /^!\[([^\]]*)\]\(([^\)]+)\)$/.exec(linkText);

                            if (imageMatch) {
                                // This is a clickable image: [![alt](imgSrc)](linkHref)
                                const altText = imageMatch[1];
                                const imgSrc = imageMatch[2];

                                if (links) {
                                    nodes.push(
                                        <Anchor
                                            key={`link-${keyRef.value++}`}
                                            href={href}
                                            onClick={() =>
                                                sendTrackEvent(
                                                    "clickedAnchor",
                                                    {
                                                        eventContainer:
                                                            "markdown",
                                                        ...analyticsAttributes,
                                                        event: "clicked",
                                                        eventComponent: "link",
                                                        href: href,
                                                        label: altText
                                                    }
                                                )
                                            }
                                        >
                                            {/* eslint-disable-next-line no-restricted-syntax */}
                                            <img src={imgSrc} alt={altText} />
                                        </Anchor>
                                    );
                                } else {
                                    nodes.push(
                                        <a
                                            key={`link-${keyRef.value++}`}
                                            href={href}
                                        >
                                            {/* eslint-disable-next-line no-restricted-syntax */}
                                            <img src={imgSrc} alt={altText} />
                                        </a>
                                    );
                                }
                            } else {
                                // Regular link with text
                                const linkChildren = parseInlineSegment(
                                    linkText,
                                    keyRef
                                );

                                if (links) {
                                    nodes.push(
                                        <Anchor
                                            key={`link-${keyRef.value++}`}
                                            href={href}
                                            onClick={() =>
                                                sendTrackEvent(
                                                    "clickedAnchor",
                                                    {
                                                        eventContainer:
                                                            "markdown",
                                                        ...analyticsAttributes,
                                                        event: "clicked",
                                                        eventComponent: "link",
                                                        href: href,
                                                        label: linkText
                                                    }
                                                )
                                            }
                                        >
                                            {linkChildren}
                                        </Anchor>
                                    );
                                } else {
                                    nodes.push(
                                        <a
                                            key={`link-${keyRef.value++}`}
                                            href={href}
                                        >
                                            {linkChildren}
                                        </a>
                                    );
                                }
                            }

                            i = endHref + 1;
                            textStart = i;
                            continue;
                        }
                    }
                }

                // Strikethrough: ~~text~~
                if (segment.startsWith("~~", i)) {
                    const closeIndex = segment.indexOf("~~", i + 2);
                    if (closeIndex !== -1) {
                        const inner = segment.slice(i + 2, closeIndex);
                        if (inner.length > 0) {
                            pushText(i);
                            nodes.push(
                                <del key={`del-${keyRef.value++}`}>
                                    {parseInlineSegment(inner, keyRef)}
                                </del>
                            );
                            i = closeIndex + 2;
                            textStart = i;
                            continue;
                        }
                    }
                }

                // Bold: **text** or __text__
                const hasStrongMarker =
                    segment.startsWith("**", i) || segment.startsWith("__", i);
                if (hasStrongMarker) {
                    const marker = segment.slice(i, i + 2);
                    const closeIndex = segment.indexOf(marker, i + 2);

                    if (closeIndex !== -1) {
                        const inner = segment.slice(i + 2, closeIndex);
                        if (inner.length > 0) {
                            pushText(i);
                            nodes.push(
                                <strong key={`strong-${keyRef.value++}`}>
                                    {parseInlineSegment(inner, keyRef)}
                                </strong>
                            );
                            i = closeIndex + 2;
                            textStart = i;
                            continue;
                        }
                    }
                }

                // Italic/Emphasis: *text* or _text_
                // Check for single * or _ (but not ** or __)
                const hasEmMarker =
                    (segment[i] === "*" && segment[i + 1] !== "*") ||
                    (segment[i] === "_" && segment[i + 1] !== "_");
                if (hasEmMarker) {
                    const marker = segment[i];
                    const closeIndex = segment.indexOf(marker, i + 1);

                    if (
                        closeIndex !== -1 &&
                        segment[i + 1] !== marker &&
                        segment[closeIndex + 1] !== marker
                    ) {
                        const inner = segment.slice(i + 1, closeIndex);
                        if (inner.length > 0 && !inner.includes("\n")) {
                            pushText(i);
                            nodes.push(
                                <em key={`em-${keyRef.value++}`}>
                                    {parseInlineSegment(inner, keyRef)}
                                </em>
                            );
                            i = closeIndex + 1;
                            textStart = i;
                            continue;
                        }
                    }
                }

                i++;
            }

            pushText(segment.length);

            return nodes;
        }
    }
};
